<script setup lang="ts">
/** Directors_ Resolutions in Writing for AGM.docx — Exempted AFS(PRS030) / Exempted UFS(PRS034). */
import CddVarField from '../../../../preset/components/final-dividends/cdd/CddVarField.vue';
import '../../../../preset/components/final-dividends/docs/agmDoc.css';
import AgmCond from '../AgmCond.vue';
import AgmDocHead from '../AgmDocHead.vue';
import AgmAuditExemptionBlock from '../AgmAuditExemptionBlock.vue';
import AgmDsSigningClause from '../AgmDsSigningClause.vue';
import AgmRetiringList from '../AgmRetiringList.vue';
import AgmSignersPreview from '../AgmSignersPreview.vue';
import { useAgmStudio } from '../agmStudioContext';

defineProps<{ variant: 'afs' | 'ufs' }>();
const { model } = useAgmStudio();
</script>

<template>
  <div class="agm-doc">
    <AgmDocHead />
    <div class="doc-title">DIRECTORS’ RESOLUTIONS IN WRITING PASSED PURSUANT TO THE COMPANY’S CONSTITUTION</div>

    <template v-if="variant === 'afs'">
      <div class="sec-title">Directors’ Statement, Auditors’ Report and the Audited Financial Statements for the Financial Year Ended <CddVarField field-key="financial_year_end_date" label="Financial Year End" /></div>
      <p class="doc-p">RESOLVED THAT, pursuant to the Companies Act 1967 (the “Act”), the Directors’ Statement, the Auditors’ Report and the Audited Financial Statements (collectively, the “Financial Statements”) of the Company for the financial year ended <CddVarField field-key="financial_year_end_date" label="Financial Year End" /> be and are hereby approved.</p>
    </template>
    <template v-else>
      <AgmAuditExemptionBlock :dash="false" />
      <div class="sec-title">Directors’ Statement and the Unaudited Financial Statements for the Financial Year Ended <CddVarField field-key="financial_year_end_date" label="Financial Year End" /></div>
      <p class="doc-p">RESOLVED THAT the Directors’ Statement and the Unaudited Financial Statements (the “Financial Statements”) of the Company for the financial year ended <CddVarField field-key="financial_year_end_date" label="Financial Year End" /> be and are hereby approved.</p>
    </template>
    <AgmDsSigningClause />

    <div class="sec-title">Exemption from Annual General Meeting under Section 175A(1A) of the Act</div>
    <p class="doc-p">It was noted that:</p>
    <p class="doc-p indent">{{ variant === 'afs' ? '•  ' : '' }}the Company is a private company; and</p>
    <p class="doc-p indent">{{ variant === 'afs' ? '•  ' : '' }}the Financial Statements will be sent to all members of the Company within 5 months after the end of the financial year.</p>
    <p class="doc-p">Accordingly, pursuant to Section 175A(1A) of the Act, the Company is exempt from the requirement to hold an Annual General Meeting for the financial year.</p>
    <p class="doc-p">RESOLVED THAT the Company shall not hold an Annual General Meeting for the financial year in reliance on Section 175A(1A) of the Act, and that the resolutions ordinarily required to be passed at an Annual General Meeting shall be passed by way of Members’ Resolutions in Writing in lieu thereof.</p>

    <div class="sec-title">Authority to E-File Returns</div>
    <p class="doc-p">RESOLVED THAT any officer of the Company or such other person(s) as directed by {{ model.directorCount === 1 ? 'the sole director' : 'the directors' }} be authorised to e-file the Annual Return for <CddVarField field-key="annual_return_year" label="Annual Return Year" /> and other relevant returns with the Accounting and Corporate Regulatory Authority, for and on behalf of the Company, pursuant to Section 197 of the Act.</p>

    <AgmCond flag="hasRetiringDirectors" label="Re-election of retiring directors">
      <div class="sec-title">Re-Election of Retiring Directors</div>
      <p class="doc-p">RESOLVED THAT, having previously consented to act as a director retiring by rotation pursuant to the Company’s Constitution, the following be and are hereby re-elected as Director(s) of the Company:</p>
      <AgmRetiringList />
    </AgmCond>

    <p class="doc-p">{{ model.directorCount === 1 ? 'Signed by the sole director of the Company:' : 'Signed by the directors of the Company:' }}</p>
    <AgmSignersPreview doc-kind="directors_resolution" caption="Director" />
    <div class="doc-meta">Dated: <CddVarField field-key="resolution_date" label="Resolution Date" /></div>
  </div>
</template>
